import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getWorkspaceIconComponent } from "@/lib/constants/workspace-customization";
import { dropdownMenuItemDestructiveClasses } from "@/lib/constants/menu-styles";
import {
  Bookmark,
  Copy,
  Folder,
  Inbox,
  MoreHorizontal,
  Pencil,
  Trash,
} from "lucide-react";
import { RiPushpinLine } from "react-icons/ri";
import PropTypes from "prop-types";

/**
 * Shared thread menu content component
 * Used by both sidebar (HistoryItem, ThreadItem) and navbar
 *
 * @param {boolean} isProjectThread - Whether this thread belongs to a project
 * @param {string} sourceWorkspaceId - Current workspace ID (for project threads, to exclude from move list)
 * @param {boolean} showBookmark - Whether to show bookmark/pin option (default: true)
 * @param {boolean} usePinTerminology - Use "Pin/Unpin" instead of "Bookmark/Remove bookmark"
 * @param {string} itemClassName - Custom class for menu items (default: rounded-xl)
 * @param {function} onMoveToChats - Handler to move project thread back to Chats
 * @param {function} onClose - Optional callback to close the menu after actions
 */
export function ThreadMenuContent({
  thread,
  workspaces,
  isProjectThread = false,
  sourceWorkspaceId = null,
  isDeleting = false,
  showBookmark = true,
  usePinTerminology = false,
  itemClassName = "rounded-xl",
  onToggleBookmark,
  onRename,
  onMoveToProject,
  onMoveToChats,
  onShare: _onShare,
  onClone,
  onDelete,
  onClose,
}) {
  const isBookmarked = thread.metadata?.bookmarked || thread.metadata?.is_bookmarked;

  // Get bookmark/pin labels based on terminology
  const bookmarkLabel = usePinTerminology
    ? (isBookmarked ? "Unpin chat" : "Pin chat")
    : (isBookmarked ? "Remove bookmark" : "Bookmark");

  // Render the appropriate icon
  const BookmarkIcon = usePinTerminology ? (
    <RiPushpinLine style={{ width: 20, height: 20, minWidth: 20, minHeight: 20 }} className="mr-2 flex-shrink-0" />
  ) : (
    <Bookmark className="h-4 w-4 mr-2" />
  );

  return (
    <>
      {/* Bookmark/Pin option - only shown when showBookmark is true */}
      {showBookmark && onToggleBookmark && (
        <DropdownMenuItem
          className={cn("flex cursor-pointer items-center dropdown-menu-item", itemClassName)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleBookmark?.(thread);
            onClose?.();
          }}
        >
          {BookmarkIcon}
          {bookmarkLabel}
        </DropdownMenuItem>
      )}

      <DropdownMenuItem
        className={cn("flex cursor-pointer items-center dropdown-menu-item", itemClassName)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRename?.(thread);
          onClose?.();
        }}
      >
        <Pencil className="h-4 w-4 mr-2" />
        Rename
      </DropdownMenuItem>

      {/* Move to project - for both history threads and project threads */}
      {onMoveToProject && (() => {
        // Filter workspaces: for project threads, exclude the current workspace
        const availableWorkspaces = isProjectThread && sourceWorkspaceId
          ? workspaces?.filter(ws => ws.id !== sourceWorkspaceId)
          : workspaces;

        // Only show if there are workspaces to show
        if (!availableWorkspaces || availableWorkspaces.length === 0) return null;

        return (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              className={cn("flex cursor-pointer items-center dropdown-menu-item whitespace-nowrap", itemClassName)}
              onSelect={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Folder className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="whitespace-nowrap">Move to project</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent
              className="w-[200px] rounded-2xl bg-background/80 backdrop-blur-md border border-border/50"
              sideOffset={5}
              alignOffset={-5}
            >
              {availableWorkspaces.map((workspace) => {
                const WorkspaceIcon = getWorkspaceIconComponent(
                  workspace.metadata?.icon || "folder"
                );
                const iconColor = workspace.metadata?.color;
                return (
                  <DropdownMenuItem
                    key={workspace.id}
                    className={cn("flex cursor-pointer items-center dropdown-menu-item", itemClassName)}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // For project threads, pass sourceWorkspaceId as third parameter
                      if (isProjectThread && sourceWorkspaceId) {
                        onMoveToProject?.(thread, workspace.id, sourceWorkspaceId);
                      } else {
                        onMoveToProject?.(thread, workspace.id);
                      }
                      onClose?.();
                    }}
                  >
                    <WorkspaceIcon
                      className="h-4 w-4 mr-2"
                      style={iconColor && iconColor !== "#f8f9fa" ? { color: iconColor } : undefined}
                    />
                    <span className="truncate">{workspace.name}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        );
      })()}

      {/* Move to Chats - only for project threads */}
      {isProjectThread && onMoveToChats && (
        <DropdownMenuItem
          className={cn("flex cursor-pointer items-center dropdown-menu-item", itemClassName)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onMoveToChats?.(thread);
            onClose?.();
          }}
        >
          <Inbox className="h-4 w-4 mr-2" />
          Move to Chats
        </DropdownMenuItem>
      )}

      <DropdownMenuItem
        className={cn("flex cursor-pointer items-center dropdown-menu-item", itemClassName)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClone?.(thread);
          onClose?.();
        }}
      >
        <Copy className="h-4 w-4 mr-2" />
        Clone
      </DropdownMenuItem>

      <DropdownMenuSeparator className="my-1" />

      <DropdownMenuItem
        className={cn(
          "flex cursor-pointer items-center dropdown-menu-item",
          dropdownMenuItemDestructiveClasses,
          itemClassName,
          isDeleting && "opacity-50 cursor-not-allowed"
        )}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isDeleting) {
            onDelete?.(thread);
            onClose?.();
          }
        }}
        disabled={isDeleting}
      >
        <Trash className="h-4 w-4 mr-2" />
        {isDeleting ? "Deleting..." : "Delete"}
      </DropdownMenuItem>
    </>
  );
}

ThreadMenuContent.propTypes = {
  thread: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    metadata: PropTypes.object,
  }).isRequired,
  workspaces: PropTypes.array,
  isProjectThread: PropTypes.bool,
  sourceWorkspaceId: PropTypes.string,
  isDeleting: PropTypes.bool,
  showBookmark: PropTypes.bool,
  usePinTerminology: PropTypes.bool,
  itemClassName: PropTypes.string,
  onToggleBookmark: PropTypes.func,
  onRename: PropTypes.func,
  onMoveToProject: PropTypes.func,
  onMoveToChats: PropTypes.func,
  onShare: PropTypes.func,
  onClone: PropTypes.func,
  onDelete: PropTypes.func,
  onClose: PropTypes.func,
};

/**
 * Full thread menu with trigger button
 * Used in the navbar
 */
export function ThreadMenu({
  thread,
  workspaces,
  isProjectThread = false,
  isDeleting = false,
  showBookmark = true,
  usePinTerminology = false,
  onToggleBookmark,
  onRename,
  onMoveToProject,
  onMoveToChats,
  onShare,
  onClone,
  onDelete,
  className,
}) {
  if (!thread) return null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-8 w-8", className)}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[200px] z-[9999] rounded-2xl bg-background/80 backdrop-blur-md border border-border/50"
        style={{ zIndex: 9999 }}
        onClick={(e) => e.stopPropagation()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <ThreadMenuContent
          thread={thread}
          workspaces={workspaces}
          isProjectThread={isProjectThread}
          isDeleting={isDeleting}
          showBookmark={showBookmark}
          usePinTerminology={usePinTerminology}
          onToggleBookmark={onToggleBookmark}
          onRename={onRename}
          onMoveToProject={onMoveToProject}
          onMoveToChats={onMoveToChats}
          onShare={onShare}
          onClone={onClone}
          onDelete={onDelete}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

ThreadMenu.propTypes = {
  thread: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    metadata: PropTypes.object,
  }),
  workspaces: PropTypes.array,
  isProjectThread: PropTypes.bool,
  isDeleting: PropTypes.bool,
  showBookmark: PropTypes.bool,
  usePinTerminology: PropTypes.bool,
  onToggleBookmark: PropTypes.func,
  onRename: PropTypes.func,
  onMoveToProject: PropTypes.func,
  onMoveToChats: PropTypes.func,
  onShare: PropTypes.func,
  onClone: PropTypes.func,
  onDelete: PropTypes.func,
  className: PropTypes.string,
};

export default ThreadMenu;
